import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { IFText, IFSkeleton, ChartComponent } from 'Components'
import { Colors } from 'Theme'
import Styles from './EnergyConsumptionChart.module.css'
import { useTranslation } from 'react-i18next'
import PropTypes from 'prop-types'
import RequestState from 'Enums/RequestState'
import {
  computeSlopes,
  quantile,
  shouldLabelDynamic,
} from 'Utils/GraphsFunctions'

const EnergyConsumptionChart = ({
  chartID = 'EnergyConsumptionChart',
  noData = false,
  chargingProgress,
  fetchEnergyConsumptionDataRequestState,
  sessionStartTime,
  sessionEndTime,
  isLive = false,
  chargingEvents = [],
}) => {
  const { t } = useTranslation()

  const { energySeriesData, sessionStartMs, sessionEndMs } = useMemo(() => {
    if (!chargingEvents || chargingEvents.length === 0) {
      const now = Date.now()
      return {
        energySeriesData: [],
        sessionStartMs: sessionStartTime
          ? new Date(sessionStartTime).getTime()
          : now,
        sessionEndMs: sessionEndTime ? new Date(sessionEndTime).getTime() : now,
      }
    }

    const allEnergyTransfers = chargingEvents
      .filter(
        (event) => event.energyTransfer && event.energyTransfer.length > 0,
      )
      .flatMap((event) => event.energyTransfer)
      .sort(
        (a, b) =>
          new Date(a.startTime).getTime() - new Date(b.startTime).getTime(),
      )

    if (allEnergyTransfers.length === 0) {
      const now = Date.now()
      return {
        energySeriesData: [],
        sessionStartMs: sessionStartTime
          ? new Date(sessionStartTime).getTime()
          : now,
        sessionEndMs: sessionEndTime ? new Date(sessionEndTime).getTime() : now,
      }
    }
    const firstTransferStart = new Date(
      allEnergyTransfers[0].startTime,
    ).getTime()

    const lastTransferEnd = isLive
      ? Date.now()
      : new Date(
          allEnergyTransfers[allEnergyTransfers.length - 1].endTime ||
            allEnergyTransfers[allEnergyTransfers.length - 1].startTime,
        ).getTime()

    return {
      energySeriesData: allEnergyTransfers,
      sessionStartMs: firstTransferStart,
      sessionEndMs: lastTransferEnd,
    }
  }, [chargingEvents, sessionStartTime, sessionEndTime, isLive])

  const [overrideStats, setOverrideStats] = useState({
    power: null,
    ampere: null,
    voltage: null,
    chargingProgress: null,
  })
  const [isHovering, setIsHovering] = useState(false)
  const [defaultStats, setDefaultStats] = useState(null)

  function findLastWithPower(data) {
    for (let i = data.length - 1; i >= 0; i--) {
      const val = data[i].power ?? data[i].y
      if (val != null && !isNaN(val)) {
        return data[i]
      }
    }
    return undefined
  }
  useEffect(() => {
    if (energySeriesData.length > 0) {
      const lastValid = findLastWithPower(energySeriesData)
      if (lastValid) {
        const stats = {
          power: ((lastValid.power ?? lastValid.y) / 1000).toFixed(2),
          ampere: lastValid.ampere ?? null,
          voltage: lastValid.volt ?? null,
          chargingProgress:
            lastValid.socEnd ??
            lastValid.socStart ??
            lastValid.chargingProgress ??
            null,
        }
        setDefaultStats(stats)

        setOverrideStats(stats)
      }
    } else if (energySeriesData.length === 0) {
      setDefaultStats({
        power: null,
        ampere: null,
        voltage: null,
        chargingProgress: null,
      })
      setOverrideStats({
        power: null,
        ampere: null,
        voltage: null,
        chargingProgress: null,
      })
    }
  }, [energySeriesData, isLive, isHovering])

  const totalDurationMinutes = useMemo(() => {
    if (energySeriesData.length === 0) return 1
    const firstTransferStartMs = new Date(
      energySeriesData[0].startTime,
    ).getTime()

    let lastTransferEndMs

    if (isLive) {
      const lastTransfer = energySeriesData[energySeriesData.length - 1]
      lastTransferEndMs = new Date(lastTransfer.endTime).getTime()
    } else {
      const lastTransfer = energySeriesData[energySeriesData.length - 1]
      lastTransferEndMs = new Date(lastTransfer.endTime).getTime()
    }

    const durationMs = lastTransferEndMs - firstTransferStartMs
    const durationMinutes = Math.max(1, durationMs / 1000 / 60)

    return durationMinutes
  }, [energySeriesData, isLive])

  const series = useMemo(() => {
    if (!energySeriesData || energySeriesData.length === 0) {
      return [{ name: 'Power', data: [] }]
    }

    const data = []
    data.push({
      x: 0,
      y: 0,
      ampere: 0,
      volt: 0,
      chargingProgress: parseFloat(
        (energySeriesData[0]?.socStart ?? 0).toFixed(0),
      ),
    })
    if (isLive) {
      let cumulativeTimeMinutes = 0

      energySeriesData.forEach((entry, index) => {
        const currentStart =
          index === 0
            ? new Date(entry.startTime).getTime()
            : new Date(energySeriesData[index - 1].endTime).getTime()

        const currentEnd = new Date(entry.endTime || entry.startTime).getTime()
        const durationMinutes = (currentEnd - currentStart) / 1000 / 60
        cumulativeTimeMinutes += durationMinutes

        data.push({
          x: parseFloat(cumulativeTimeMinutes.toFixed(2)),
          y: parseFloat(((entry.power ?? 0) / 1000).toFixed(2)),
          ampere: entry.ampere ?? 0,
          volt: entry.volt ?? 0,
          chargingProgress: parseFloat(
            (entry.socEnd ?? entry.socStart ?? 0).toFixed(0),
          ),
        })
      })
    } else {
      const sessionStart = new Date(energySeriesData[0].startTime).getTime()

      energySeriesData.forEach((entry) => {
        const endTime = new Date(entry.endTime || entry.startTime).getTime()
        const elapsedMinutes = (endTime - sessionStart) / 1000 / 60

        data.push({
          x: parseFloat(elapsedMinutes.toFixed(2)),
          y: parseFloat(((entry.power ?? 0) / 1000).toFixed(2)),
          ampere: entry.ampere ?? 0,
          volt: entry.volt ?? 0,
          chargingProgress: parseFloat(
            (entry.socEnd ?? entry.socStart ?? 0).toFixed(0),
          ),
        })
      })
    }
    return [{ name: 'Power', data }]
  }, [energySeriesData, isLive])

  const { slopes, threshold } = useMemo(() => {
    const pts = series[0].data
    if (pts.length < 2) return { slopes: [], threshold: 0 }
    const sl = computeSlopes(pts).map(Math.abs)
    const th = quantile(sl, 0.6)
    return { slopes: sl, threshold: th }
  }, [series])

  const xAxisLabelFormatter = useCallback(
    (val) => {
      const step = stepForMins(totalDurationMinutes)
      const eps = 5
      const mod = val % step
      const near = mod < eps || step - mod < eps
      if (!near && val !== 0 && Math.abs(val - totalDurationMinutes) > eps)
        return ''

      if (val < 60) return `${val.toFixed(step >= 5 ? 0 : 1)} min`
      const h = Math.floor(val / 60)
      const m = Math.round(val % 60)
      return m ? `${h}h ${m}min` : `${h}h`
    },
    [totalDurationMinutes],
  )

  const stepForMins = (mins) => {
    if (mins <= 6) return 1
    if (mins <= 30) return 5
    if (mins <= 120) return 15
    return 60
  }
  const [options, setOptions] = useState({
    chart: {
      id: chartID,
      type: 'area',
      zoom: { enabled: false },
      toolbar: { show: false },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 500,
      },
      events: {
        mouseMove: function (event, chartContext, config) {
          const dataPointIndex = config?.dataPointIndex
          const seriesIndex = config?.seriesIndex
          if (
            typeof dataPointIndex === 'number' &&
            dataPointIndex >= 0 &&
            chartContext?.w?.globals?.series?.[seriesIndex]?.[dataPointIndex] !=
              null
          ) {
            const pt =
              chartContext.w.globals.initialSeries[seriesIndex].data[
                dataPointIndex
              ]
            setOverrideStats({
              power: pt.y.toFixed(2),
              ampere: pt.ampere ?? null,
              voltage: pt.volt ?? null,
              chargingProgress: pt.chargingProgress
                ? pt.chargingProgress.toFixed(0)
                : null,
            })
            setIsHovering(true)
          }
        },
        dataPointMouseEnter: (
          _e,
          chartContext,
          { seriesIndex, dataPointIndex },
        ) => {
          const pt1 =
            chartContext.w.config.series[seriesIndex].data[dataPointIndex]
          setOverrideStats({
            power: pt1.y.toFixed(2),
            ampere: pt1.ampere,
            voltage: pt1.volt,
            chargingProgress: pt1.chargingProgress?.toFixed(0),
          })
        },
        mouseLeave: () => {
          if (defaultStats) {
            setOverrideStats(defaultStats)
          }
          setIsHovering(false)
        },
      },
    },
    colors: [Colors.primary],
    stroke: { curve: 'smooth', width: 2 },
    dataLabels: {
      enabled: true,
      offsetY: -6,
      offsetX: 4,
      formatter: (_val, opts) => {
        const idx = opts.dataPointIndex
        const pts = opts.w.globals.initialSeries[opts.seriesIndex].data
        const current = pts[idx].chargingProgress
        const prev = pts[idx - 1]?.chargingProgress

        if (isLive) {
          if (idx === pts.length - 1 || current !== prev) {
            return current > 0 ? current + '%' : ''
          }
          return ''
        } else {
          if (idx === pts.length - 1) {
            return current > 0 ? current + '%' : ''
          }
          if (shouldLabelDynamic && shouldLabelDynamic(pts, idx, threshold)) {
            return pts[idx].chargingProgress > 0
              ? pts[idx].chargingProgress + '%'
              : ''
          }
          return ''
        }
      },
      style: {
        fontSize: '0.75rem',
        fontFamily: 'ProximaNova',
        colors: [Colors.primary],
      },
    },
    xaxis: {
      type: 'numeric',
      decimalsInFloat: 2,
      tickAmount: 6,
      min: 0,
      max: totalDurationMinutes,
      labels: {
        formatter: (val) => {
          const step = stepForMins(totalDurationMinutes)
          const eps = 1
          const mod = val % step
          const near = mod < eps || step - mod < eps
          if (!near && val !== 0 && Math.abs(val - totalDurationMinutes) > eps)
            return ''

          if (val < 60) return `${val.toFixed(step >= 5 ? 0 : 1)} min`
          const h = Math.floor(val / 60)
          const m = Math.round(val % 60)
          return m ? `${h}h ${m}min` : `${h}h`
        },
      },
    },
    yaxis: { min: 0, tickAmount: 5 },
    tooltip: { enabled: true, shared: true, theme: 'dark' },
    grid: { show: false },
    markers: { size: 0 },
  })

  useEffect(() => {
    setOptions((prev) => ({
      ...prev,
      xaxis: {
        ...prev.xaxis,
        tickAmount: Math.min(series[0].data.length + 1, 6),
        min: 0,
        max: totalDurationMinutes > 0 ? totalDurationMinutes : 5,
        labels: {
          ...prev.xaxis.labels,
          formatter: xAxisLabelFormatter,
        },
      },
    }))
  }, [totalDurationMinutes, series, xAxisLabelFormatter])

  return (
    <div id="chart" className={Styles.WrapperContainer}>
      <div>
        <div className={Styles.MapHeader}>
          <IFText
            className={Styles.StatisticsSubTitle}
            style={{ color: Colors.text }}
            loadSkeleton={
              fetchEnergyConsumptionDataRequestState === RequestState.LOADING
            }
            skeletonWidth={150}
            skeletonHeight={20}
          >
            {t('TransactionEvents.PowerOverTime')}
          </IFText>
        </div>

        <div className={Styles.GraphContainer}>
          {fetchEnergyConsumptionDataRequestState === RequestState.LOADING ? (
            <div className={Styles.ChartSkeleton}>
              <IFSkeleton width={680} height={240} />
            </div>
          ) : (
            <>
              <ChartComponent
                options={options}
                series={series}
                height={200}
                width={680}
                type="area"
                className={noData ? Styles.OverlayTest : Styles.Chart}
              />
              {noData && (
                <IFText
                  className={Styles.OverlayText}
                  style={{ color: Colors.red }}
                >
                  {t('ConnectorUtilization.NoData')}
                </IFText>
              )}
            </>
          )}
        </div>
      </div>

      <div className={Styles.DetailsDiv}>
        <IFText
          className={Styles.StatisticsSubTitle}
          loadSkeleton={
            fetchEnergyConsumptionDataRequestState === RequestState.LOADING
          }
          style={{ color: Colors.text }}
          skeletonWidth={150}
          skeletonHeight={20}
        >
          {t('TransactionEvents.ChargingStatistics')}
        </IFText>

        <div className={Styles.ContentDiv}>
          <div>
            <div className={Styles.TopDetails}>
              <div>
                <IFText
                  className={Styles.DetailsHeader}
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={100}
                  skeletonHeight={20}
                >
                  {t('TransactionEvents.Power')}
                </IFText>
                <IFText
                  className={`${Styles.DetailsText} ${
                    isLive ? Styles.livePulse : ''
                  }`}
                  style={
                    isHovering || isLive
                      ? { color: Colors.primary, fontSize: 20 }
                      : {}
                  }
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={150}
                  skeletonHeight={20}
                >
                  {overrideStats.power ?? '—'}
                </IFText>
              </div>
              <div>
                <IFText
                  className={Styles.DetailsHeader}
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={100}
                  skeletonHeight={20}
                >
                  {t('TransactionEvents.Ampere')}
                </IFText>
                <IFText
                  className={`${Styles.DetailsText} ${
                    isLive ? Styles.livePulse : ''
                  }`}
                  style={
                    isHovering || isLive
                      ? { color: Colors.primary, fontSize: 20 }
                      : {}
                  }
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={150}
                  skeletonHeight={20}
                >
                  {overrideStats.ampere ?? '—'}
                </IFText>
              </div>
            </div>

            <div
              style={{ backgroundColor: Colors.grey }}
              className={Styles.VerticalDivider}
            />

            <div className={Styles.TopDetails}>
              <div>
                <IFText
                  className={Styles.DetailsHeader}
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={100}
                  skeletonHeight={20}
                >
                  {t('TransactionEvents.ChargingProgress')}
                </IFText>
                <IFText
                  className={`${Styles.DetailsText} ${
                    isLive ? Styles.livePulse : ''
                  }`}
                  style={
                    isHovering || isLive
                      ? { color: Colors.primary, fontSize: 20 }
                      : {}
                  }
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={150}
                  skeletonHeight={20}
                >
                  {overrideStats.chargingProgress != null
                    ? Number(overrideStats.chargingProgress).toFixed(0) +
                      t('TransactionEvents.Percentage')
                    : chargingProgress != null
                    ? Number(chargingProgress).toFixed(0) +
                      t('TransactionEvents.Percentage')
                    : '—'}
                </IFText>
              </div>

              <div>
                <IFText
                  className={Styles.DetailsHeader}
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={100}
                  skeletonHeight={20}
                >
                  {t('TransactionEvents.Voltage')}
                </IFText>
                <IFText
                  className={`${Styles.DetailsText} ${
                    isLive ? Styles.livePulse : ''
                  }`}
                  style={
                    isHovering || isLive
                      ? { color: Colors.primary, fontSize: 20 }
                      : {}
                  }
                  loadSkeleton={
                    fetchEnergyConsumptionDataRequestState ===
                    RequestState.LOADING
                  }
                  skeletonWidth={150}
                  skeletonHeight={20}
                >
                  {overrideStats.voltage ?? '—'}
                </IFText>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

EnergyConsumptionChart.propTypes = {
  energySeriesData: PropTypes.array.isRequired,
  chartID: PropTypes.string,
  noData: PropTypes.bool,
  chargingProgress: PropTypes.number,
  fetchEnergyConsumptionDataRequestState: PropTypes.number.isRequired,
  sessionStartTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]),
  sessionEndTime: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.instanceOf(Date),
    PropTypes.number,
  ]),
  isLive: PropTypes.bool,
}

export default EnergyConsumptionChart
